import type { LegalDocument } from '../../legal/types/legalDocument';

interface LegalDocumentModalProps {
  document: LegalDocument;
  isOpen: boolean;
  onClose: () => void;
}

export default function LegalDocumentModal({
  document,
  isOpen,
  onClose,
}: LegalDocumentModalProps) {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#020817]/90 px-2 py-2 backdrop-blur-xl sm:px-3 sm:py-3 md:px-4 md:py-4">
      <div className="flex h-[calc(100dvh-1rem)] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-violet-500/25 bg-[#07111f] shadow-[0_0_80px_rgba(139,92,246,0.18)] sm:h-[96dvh] sm:rounded-3xl">
        <header className="shrink-0 border-b border-violet-500/20 bg-[#0f172a]/90 px-4 py-2.5 sm:px-5 sm:py-3 md:px-6">
          <div className="flex flex-col gap-2.5 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-violet-300">
                Documento institucional
              </p>

              <h2 className="mt-1 text-xl font-bold text-white md:text-2xl">
                {document.title}
              </h2>

              <dl className="mt-2 grid gap-x-3 gap-y-1.5 text-xs text-slate-300 min-[420px]:grid-cols-3 sm:text-sm">
                <div>
                  <dt className="text-xs uppercase tracking-[0.16em] text-slate-500">
                    Versão
                  </dt>
                  <dd className="mt-1 font-medium text-slate-200">
                    {document.version}
                  </dd>
                </div>

                <div>
                  <dt className="text-xs uppercase tracking-[0.16em] text-slate-500">
                    Última atualização
                  </dt>
                  <dd className="mt-1 font-medium text-slate-200">
                    {document.lastUpdated}
                  </dd>
                </div>

                <div>
                  <dt className="text-xs uppercase tracking-[0.16em] text-slate-500">
                    Vigência
                  </dt>
                  <dd className="mt-1 font-medium text-slate-200">
                    {document.effectiveDate}
                  </dd>
                </div>
              </dl>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 shrink-0 items-center justify-center rounded-2xl border border-violet-500/25 bg-violet-500/10 px-5 text-sm font-semibold text-violet-200 transition-all duration-300 hover:border-violet-400/45 hover:bg-violet-500/15 hover:text-white"
            >
              Fechar
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 md:px-6 md:py-6">
          <article className="mx-auto max-w-4xl">
            <h1 className="text-3xl font-bold text-white">
              {document.title}
            </h1>

            <div className="mt-7 space-y-9">
              {document.sections.map((section) => (
                <section key={section.id}>
                  <h2 className="text-xl font-semibold text-slate-100">
                    {section.title}
                  </h2>

                  {section.subtitle && (
                    <p className="mt-2 text-sm font-medium text-violet-300">
                      {section.subtitle}
                    </p>
                  )}

                  <div className="mt-4 space-y-4">
                    {section.paragraphs.map((paragraph) => (
                      <p
                        key={paragraph.id}
                        className="text-[15px] leading-7 text-slate-300"
                      >
                        {paragraph.text}
                      </p>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </article>
        </div>

        <footer className="shrink-0 border-t border-violet-500/20 bg-[#0f172a]/90 px-5 py-2 text-right md:px-6 md:py-2.5">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 items-center justify-center rounded-2xl bg-gradient-to-r from-violet-600 to-purple-500 px-7 text-sm font-semibold text-white shadow-[0_0_34px_rgba(139,92,246,0.28)] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_48px_rgba(139,92,246,0.42)]"
          >
            Fechar
          </button>
        </footer>
      </div>
    </div>
  );
}
